import InventoriesApiV2 from 'api/v2/admins/inventories-api-v2'
import ProductsApiV2 from 'api/v2/admins/products-api-v2'
import TitlePage from 'components/atoms/title-page'
import { ALERT_TYPES } from 'constants/alert-constants'
import InventoryTable from 'domains/inventory/organisms/inventory-table'
import BulkMarkSoldOnHoldModal from 'domains/inventory/organisms/bulk-mark-sold-on-hold-modal/bulk-mark-sold-on-hold-modal'
import SendPhotoLinkEmailModal from 'domains/inventory/organisms/send-photo-link-email-modal/send-photo-link-email-modal'
import SwapBkrToBkxConfirmationModal from 'domains/product/organisms/swap-bkr-to-bkx-confirmation-modal/swap-bkr-to-bkx-confirmation-modal'
import {
  PHASE3_JULY20_APPRAISAL_CATEGORIES,
  PHASE3_JULY20_APPRAISAL_CATEGORY_LABELS,
  phase3July20CanViewAppraisalNeeded,
  phase3July20CanViewAppraisalApproval,
  phase3July20CanViewAllInventory,
  phase3July20FixedCategoryForUser,
  phase3July20ShowCategoryDropdown
} from 'domains/phase3-inbound-journey/constants'
import { normalizeInventoryPricePermissions } from 'domains/inventory/utils/inventory-price-permission-util'
import PropTypes from 'prop-types'
import React, { useCallback, useState, useMemo, useRef } from 'react'
import { useHistory, useLocation } from 'react-router'
import { useAuth } from 'providers/auth-provider'
import { Button, Card, CardBody, Form, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'

const propTypes = {
  pageUtils: PropTypes.shape({
    setAlertMsg    : PropTypes.func,
    setApiErrorMsg : PropTypes.func
  })
}

export const INVENTORY_LIST_URL_FILTER_KEYS = [
  'product_status',
  'product_curator_pic',
  'category_id',
  'condition_id',
  'internal_category',
  'p_size',
  'photo_link_email_sent'
]

const INVENTORY_WORKFLOW_OPTIONS = [
  { value: 'appraisal_needed', label: 'Appraisal Needed', access: 'needed' },
  { value: 'appraisal_approval', label: 'Appraisal Approve', access: 'approval' },
  { value: 'appraisal_done', label: 'Appraisal Done', access: 'needed' }
]

const MANUAL_DELETE_REASON_OPTIONS = [
  { value: 'fake_product', label: 'Fake / Not Authentic' },
  { value: 'orphaned_bko', label: 'Cancelled / Orphaned BKO' },
  { value: 'duplicate_or_wrong_product', label: 'Duplicate / Wrong Product' },
  { value: 'other', label: 'Other' }
]

const getCurrentSearchParams = () => new URLSearchParams(window.location.search)

const getInventoryListUrlFilters = () => {
  const searchParams = getCurrentSearchParams()

  return INVENTORY_LIST_URL_FILTER_KEYS
    .map((filterKey) => ({
      id    : filterKey,
      value : searchParams.get(filterKey)
    }))
    .filter((filter) => filter.value)
}

const getInventoryListUrlSearch = () => getCurrentSearchParams().get('q') || ''

const syncInventoryListUrl = ({ history, q = '', filters = [], resetPage = false }) => {
  const searchParams = getCurrentSearchParams()
  const keyword      = q.trim()
  if (resetPage) searchParams.set('page', '1')

  if (keyword) searchParams.set('q', keyword)
  else searchParams.delete('q')

  INVENTORY_LIST_URL_FILTER_KEYS.forEach((filterKey) => searchParams.delete(filterKey))

  filters.forEach((filter) => {
    if (!INVENTORY_LIST_URL_FILTER_KEYS.includes(filter.id)) return
    if (!filter.value) return

    searchParams.set(filter.id, filter.value)
  })

  const search  = searchParams.toString()
  const nextUrl = `${window.location.pathname}${search ? `?${search}` : ''}`

  history.replace({
    pathname : window.location.pathname,
    search   : search ? `?${search}` : ''
  })

  return nextUrl
}

const InventoryListPage = ({ pageUtils }) => {
  const [ inventories, setInventories ]               = useState([])
  const [ pagination, setPagination ]                 = useState({})
  const [ isLoading, setIsLoading ]                   = useState(false)
  const [ generalSearchInput, setGeneralSearchInput ] = useState(getInventoryListUrlSearch)
  const [ generalSearch, setGeneralSearch ]           = useState(getInventoryListUrlSearch)
  const [ tableFilters, setTableFilters ]             = useState(getInventoryListUrlFilters)
  const [ searchVersion, setSearchVersion ]           = useState(0)
  const [ pricePermissions, setPricePermissions ]     = useState({})
  const [ inventoryPermissions, setInventoryPermissions ] = useState({})
  const [ filterOptions, setFilterOptions ]           = useState({})
  const [ pendingManualDelete, setPendingManualDelete ] = useState(null)
  const [ manualDeleteReason, setManualDeleteReason ] = useState('')
  const [ manualDeleteNote, setManualDeleteNote ]     = useState('')
  const [ pendingMarkStatus, setPendingMarkStatus ]       = useState(null)
  const [ markStatusAction, setMarkStatusAction ]         = useState('sold')
  const [ isMarkingStatus, setIsMarkingStatus ]           = useState(false)
  const [ isBulkMarkStatusOpen, setIsBulkMarkStatusOpen ] = useState(false)
  const [ isManualDeleting, setIsManualDeleting ]     = useState(false)
  const [ pendingUndoDelete, setPendingUndoDelete ]   = useState(null)
  const [ isUndoDeleting, setIsUndoDeleting ]         = useState(false)
  const [ pendingBkrToBkx, setPendingBkrToBkx ]       = useState(null)
  const [ pendingPhotoLinkEmail, setPendingPhotoLinkEmail ] = useState(null)
  const searchVersionRef                              = useRef(0)

  const history = useHistory()
  const location = useLocation()
  const { user } = useAuth()
  const canViewAllInventory = phase3July20CanViewAllInventory(user)
  const searchParams = new URLSearchParams(location.search)
  const fixedAppraisalCategory = phase3July20FixedCategoryForUser(user)
  const showAppraisalCategoryDropdown = phase3July20ShowCategoryDropdown(user)
  const requestedAppraisalCategory = searchParams.get('appraisal_category') || ''
  const selectedAppraisalCategory = PHASE3_JULY20_APPRAISAL_CATEGORIES.includes(requestedAppraisalCategory)
    ? requestedAppraisalCategory
    : ''
  const effectiveAppraisalCategory = fixedAppraisalCategory || (showAppraisalCategoryDropdown ? selectedAppraisalCategory : '')
  const availableWorkflowOptions = INVENTORY_WORKFLOW_OPTIONS.filter((option) => {
    return option.access === 'needed'
      ? phase3July20CanViewAppraisalNeeded(user)
      : phase3July20CanViewAppraisalApproval(user)
  })
  const requestedWorkflow = searchParams.get('workflow') || ''
  const requestedWorkflowOption = availableWorkflowOptions.find((option) => option.value === requestedWorkflow)
  const defaultRestrictedWorkflowOption = availableWorkflowOptions.find((option) => option.value === 'appraisal_needed')
  const activeWorkflowOption = requestedWorkflowOption || (!canViewAllInventory ? defaultRestrictedWorkflowOption : undefined)
  const activeWorkflow = activeWorkflowOption?.value || ''
  const normalizedPricePermissions = normalizeInventoryPricePermissions(pricePermissions)
  const searchPlaceholder = normalizedPricePermissions.canViewSupplierName
    ? 'Cari berdasarkan SKU, nama produk, atau nama consignor/seller'
    : 'Cari berdasarkan SKU atau nama produk'
  const searchHelperText = normalizedPricePermissions.canViewSupplierName
    ? 'Filter Status Produk default ke Semua. Pencarian dapat digabungkan dengan SKU, Nama Produk, atau Nama Consignor/Seller.'
    : 'Filter Status Produk default ke Semua. Pencarian dapat digabungkan dengan SKU atau Nama Produk.'

  const hiddenColumns = useMemo(() => {
    const columns = []

    if (!normalizedPricePermissions.canViewSupplierName) {
      columns.push(
        'seller_name',
        'supplier_name',
        'consignor_name',
        'consignor_seller_name'
      )
    }

    if (!normalizedPricePermissions.canViewBuyingPrice) {
      columns.push('buying_price')
    }

    if (!normalizedPricePermissions.canViewDefaultPrice) {
      columns.push('default_price')
    }

    if (!normalizedPricePermissions.canViewSellingPrice) {
      columns.push('selling_price')
    }

    return columns
  }, [
    normalizedPricePermissions.canViewBuyingPrice,
    normalizedPricePermissions.canViewDefaultPrice,
    normalizedPricePermissions.canViewSellingPrice,
    normalizedPricePermissions.canViewSupplierName
  ])

  const bumpSearchVersion = useCallback(() => {
    searchVersionRef.current += 1
    setSearchVersion(searchVersionRef.current)
  }, [])

  const handleFetchInventories = useCallback((tableState) => {
    if (searchVersion !== searchVersionRef.current) return

    const additionalFilters = {
      skip_product_photo          : true,
      phase3_appraisal_access     : true
    }
    if (effectiveAppraisalCategory) additionalFilters.appraisal_category = effectiveAppraisalCategory
    if (activeWorkflow) additionalFilters.workflow = activeWorkflow
    const keyword = generalSearch.trim()
    const filters = tableState?.filters || []

    setTableFilters(filters)
    syncInventoryListUrl({ history, q: keyword, filters })

    if (keyword) additionalFilters.q = keyword

    setIsLoading(true)
    InventoriesApiV2.get({ tableState: tableState, additionalFilters: additionalFilters })
      .then((response) => {
        if (searchVersion !== searchVersionRef.current) return

        setInventories(response.data.inventories)
        setPagination(response.data.meta)
        setPricePermissions(normalizeInventoryPricePermissions(response.data))
        setInventoryPermissions(response.data.permissions || {})
        setFilterOptions(response.data.filter_options || {})
      })
      .catch((error) => {
        if (searchVersion !== searchVersionRef.current) return

        pageUtils.setApiErrorMsg(error.response?.data || { message: 'Failed to fetch inventories.' })
      })
      .finally(() => {
        if (searchVersion !== searchVersionRef.current) return

        setIsLoading(false)
      })
  }, [ activeWorkflow, effectiveAppraisalCategory, generalSearch, history, pageUtils, searchVersion ])

  const clearInventorySearch = useCallback(() => {
    setGeneralSearchInput('')
    setGeneralSearch('')
    syncInventoryListUrl({ history, q: '', filters: tableFilters, resetPage: true })
    bumpSearchVersion()
  }, [ bumpSearchVersion, history, tableFilters ])

  const handleSearchInputChange = (event) => {
    const nextValue = event.target.value

    setGeneralSearchInput(nextValue)

    if (!nextValue && generalSearch) {
      setGeneralSearch('')
      syncInventoryListUrl({ history, q: '', filters: tableFilters, resetPage: true })
      bumpSearchVersion()
    }
  }

  const handleSearchSubmit = (event) => {
    event.preventDefault()

    const keyword = generalSearchInput.trim()

    setGeneralSearch(keyword)
    syncInventoryListUrl({ history, q: keyword, filters: tableFilters, resetPage: true })
    bumpSearchVersion()
  }

  const handleClearSearch = () => {
    clearInventorySearch()
  }

  const handleWorkflowChange = (event) => {
    const nextWorkflow = event.target.value
    const nextSearchParams = new URLSearchParams(location.search)

    if (nextWorkflow) nextSearchParams.set('workflow', nextWorkflow)
    else nextSearchParams.delete('workflow')

    nextSearchParams.delete('page')
    nextSearchParams.delete('per_page')

    history.push({
      pathname : location.pathname,
      search   : nextSearchParams.toString() ? `?${nextSearchParams.toString()}` : ''
    })
    bumpSearchVersion()
  }

  const handleAppraisalCategoryChange = (event) => {
    const nextCategory = event.target.value
    const nextSearchParams = new URLSearchParams(location.search)

    if (nextCategory) nextSearchParams.set('appraisal_category', nextCategory)
    else nextSearchParams.delete('appraisal_category')

    nextSearchParams.delete('page')
    nextSearchParams.delete('per_page')

    history.push({
      pathname : location.pathname,
      search   : nextSearchParams.toString() ? `?${nextSearchParams.toString()}` : ''
    })
    bumpSearchVersion()
  }

  // Goes to Inventory Detail Page when clicking on the row button
  const handleSelectRow = useCallback((datum) => {
    const id = datum.id
    const inventoryListReturnUrl = syncInventoryListUrl({ history, q: generalSearch, filters: tableFilters })

    history.push({
      pathname : `${id}`,
      state    : {
        inventoryListReturnUrl
      }
    })
  }, [ generalSearch, history, tableFilters ])

  const canManualDelete = inventoryPermissions.can_manual_delete === true
  const canUndoDelete = inventoryPermissions.can_undo_delete === true
  const canMarkSoldOnHold = inventoryPermissions.can_mark_sold_on_hold === true

  const openMarkSoldOnHold = useCallback((inventory) => {
    setPendingMarkStatus(inventory)
    setMarkStatusAction(inventory.sellable_quantity > 0 ? 'on_hold' : 'sold')
  }, [])

  const closeMarkSoldOnHold = () => {
    if (isMarkingStatus) return
    setPendingMarkStatus(null)
  }

  const submitMarkSoldOnHold = async () => {
    if (!pendingMarkStatus || isMarkingStatus) return

    setIsMarkingStatus(true)
    try {
      const response = await InventoriesApiV2.markSoldOnHold({
        inventory_id : pendingMarkStatus.id,
        action       : markStatusAction
      })
      pageUtils.setAlertMsg(response.data?.message || 'Inventory status updated.')
      setPendingMarkStatus(null)
      bumpSearchVersion()
    } catch (error) {
      pageUtils.setApiErrorMsg(error.response?.data || { messages: { base: error.message } })
    } finally {
      setIsMarkingStatus(false)
    }
  }

  const openManualDelete = useCallback((inventory) => {
    setPendingManualDelete(inventory)
    setManualDeleteReason('')
    setManualDeleteNote('')
  }, [])

  const closeManualDelete = () => {
    if (isManualDeleting) return

    setPendingManualDelete(null)
    setManualDeleteReason('')
    setManualDeleteNote('')
  }

  const submitManualDelete = async () => {
    if (!pendingManualDelete || !manualDeleteReason || isManualDeleting) return

    if (manualDeleteReason === 'other' && !manualDeleteNote.toString().trim()) {
      pageUtils.setApiErrorMsg({ messages: { base: 'Note wajib diisi untuk reason Other.' } })
      return
    }

    setIsManualDeleting(true)
    try {
      const response = await InventoriesApiV2.manualDelete({
        inventory_id : pendingManualDelete.id,
        reason       : manualDeleteReason,
        note         : manualDeleteNote
      })

      pageUtils.setAlertMsg(response.data?.message || 'Inventory manually deleted.')
      setPendingManualDelete(null)
      setManualDeleteReason('')
      setManualDeleteNote('')
      bumpSearchVersion()
    } catch (error) {
      pageUtils.setApiErrorMsg(error.response?.data || { messages: { base: error.message } })
    } finally {
      setIsManualDeleting(false)
    }
  }

  const openUndoDelete = useCallback((inventory) => {
    setPendingUndoDelete(inventory)
  }, [])

  const closeUndoDelete = () => {
    if (isUndoDeleting) return

    setPendingUndoDelete(null)
  }

  const submitUndoDelete = async () => {
    if (!pendingUndoDelete || isUndoDeleting) return

    setIsUndoDeleting(true)
    try {
      const response = await InventoriesApiV2.undoDelete({
        inventory_id: pendingUndoDelete.id
      })

      pageUtils.setAlertMsg(response.data?.message || 'Inventory Undo Delete successful.')
      setPendingUndoDelete(null)
      bumpSearchVersion()
    } catch (error) {
      pageUtils.setApiErrorMsg(error.response?.data || { messages: { base: error.message } })
    } finally {
      setIsUndoDeleting(false)
    }
  }

  const openBkrToBkx = useCallback((inventory) => {
    setPendingBkrToBkx(inventory)
  }, [])

  const closeBkrToBkx = () => {
    setPendingBkrToBkx(null)
  }

  const submitBkrToBkx = async (deletionNote) => {
    if (!pendingBkrToBkx) return

    const productIdentifier = pendingBkrToBkx.product_sku || pendingBkrToBkx.inventory_sku
    if (!productIdentifier) {
      pageUtils.setApiErrorMsg({ messages: { base: 'Product identifier tidak ditemukan untuk inventory ini.' } })
      setPendingBkrToBkx(null)
      return
    }

    try {
      const response = await ProductsApiV2.swapBkrToBkx({
        id           : productIdentifier,
        deletionNote : deletionNote
      })

      pageUtils.setAlertMsg(response.data?.message || 'Product marked as BKX.')
      setPendingBkrToBkx(null)
      bumpSearchVersion()
      return response
    } catch (error) {
      pageUtils.setApiErrorMsg(error.response?.data || { messages: { base: error.message } })
      throw error
    }
  }

  const openPhotoLinkEmail = useCallback((inventory) => {
    setPendingPhotoLinkEmail({
      id                      : inventory.product_id,
      sku                     : inventory.product_sku,
      photo_folder_link       : inventory.photo_folder_link,
      consignor_email         : inventory.consignor_email,
      photo_link_email_sent   : inventory.photo_link_email_sent,
      photo_link_email_sent_at: inventory.photo_link_email_sent_at,
      photo_link_email_sent_by: inventory.photo_link_email_sent_by
    })
  }, [])

  const inventoryActionButtons = useMemo(() => {
    const buttons = []

    if (canMarkSoldOnHold) {
      buttons.push({
        key       : 'mark-sold-on-hold',
        text      : 'Sold / Hold',
        color     : 'info',
        outline   : false,
        icon      : 'fas fa-dollar-sign',
        title     : 'Mark as Sold / On Hold',
        isVisible : (inventory) => inventory.mark_sold_on_hold_status !== 'sold' && inventory.consignor_return_status !== 'returned',
        onClick   : openMarkSoldOnHold
      })
    }

    if (canManualDelete) {
      buttons.push({
        key       : 'manual-delete',
        text      : 'Manual Delete',
        color     : 'danger',
        outline   : false,
        icon      : 'fas fa-trash-alt',
        title     : 'Manual Delete (owner only)',
        isVisible : (inventory) => inventory.can_manual_delete === true,
        onClick   : openManualDelete
      })
    }

    if (canUndoDelete) {
      buttons.push({
        key       : 'undo-delete',
        text      : 'Undo Delete',
        color     : 'warning',
        outline   : false,
        icon      : 'fas fa-undo',
        title     : 'Undo Delete (owner only)',
        isVisible : (inventory) => inventory.can_undo_delete === true,
        onClick   : openUndoDelete
      })
    }

    buttons.push({
      key       : 'send-photo-link',
      text      : 'Send Photo Link',
      color     : 'warning',
      outline   : true,
      icon      : 'fas fa-camera',
      title     : 'Input Photo Link and Send Email',
      isVisible : (inventory) => inventory.can_send_photo_link_email === true,
      onClick   : openPhotoLinkEmail
    })

    buttons.push({
      key       : 'mark-bkx',
      text      : 'Mark BKX',
      color     : 'danger',
      outline   : true,
      icon      : 'fas fa-ban',
      title     : 'Mark confirmed fake BKR as BKX',
      isVisible : (inventory) => inventory.can_swap_bkr_to_bkx === true,
      onClick   : openBkrToBkx
    })

    return buttons
  }, [ canManualDelete, canMarkSoldOnHold, canUndoDelete, openManualDelete, openUndoDelete, openBkrToBkx, openPhotoLinkEmail ])

  const inventoryViewControl = availableWorkflowOptions.length > 0 ? (
    <div className='mb-2 mb-lg-0 mr-lg-3' style={{ minWidth: 210 }}>
      <Input type='select' value={ activeWorkflow } onChange={ handleWorkflowChange }>
        { canViewAllInventory && <option value=''>All Inventory</option> }
        { availableWorkflowOptions.map((option) => (
          <option key={ option.value } value={ option.value }>{ option.label }</option>
        )) }
      </Input>
    </div>
  ) : null

  return (
    <>
      <TitlePage mainTitle={ 'Inventory List' } />

      <Card>
        <CardBody className='pb-3'>
          <Form onSubmit={ handleSearchSubmit }>
            <div className='d-flex flex-column flex-lg-row align-items-lg-center'>
              { inventoryViewControl }
              { showAppraisalCategoryDropdown && (
                <div className='mb-2 mb-lg-0 mr-lg-3' style={{ minWidth: 190 }}>
                  <Input
                    type='select'
                    value={ selectedAppraisalCategory }
                    onChange={ handleAppraisalCategoryChange }
                  >
                    <option value=''>All H/W/C</option>
                    { PHASE3_JULY20_APPRAISAL_CATEGORIES.map((category) => (
                      <option key={ category } value={ category }>
                        { PHASE3_JULY20_APPRAISAL_CATEGORY_LABELS[category] || category }
                      </option>
                    )) }
                  </Input>
                </div>
              ) }

              <Input
                className='flex-grow-1'
                type='search'
                value={ generalSearchInput }
                onChange={ handleSearchInputChange }
                placeholder={ searchPlaceholder }
                autoCapitalize='characters'
                autoComplete='off'
                enterKeyHint='search'
                style={{ height: '42px' }}
              />

              <div className='d-flex align-items-center mt-2 mt-lg-0 ml-lg-3'>
                <Button
                  color='primary'
                  type='submit'
                  className='mr-2 px-4'
                  style={{ height: '42px' }}
                >
                  Search
                </Button>
                <Button
                  color='secondary'
                  outline
                  type='button'
                  className='px-4'
                  style={{ height: '42px' }}
                  onClick={ handleClearSearch }
                >
                  Clear
                </Button>
                { canMarkSoldOnHold && (
                  <Button
                    color='info'
                    type='button'
                    className='ml-2 px-2'
                    style={{ height: '42px', fontSize: '11px', lineHeight: 1.1, whiteSpace: 'nowrap', flexShrink: 0 }}
                    onClick={ () => setIsBulkMarkStatusOpen(true) }
                  >
                    Bulk Sold/Hold
                  </Button>
                ) }
              </div>
            </div>

            <small className='d-block text-muted mt-2'>{ searchHelperText }</small>
          </Form>
        </CardBody>

        <InventoryTable
          key={ `inventory-list-${searchVersion}` }
          data={ inventories }
          pagination={ pagination }
          isLoading={ isLoading }
          initialFilter={ tableFilters }
          hiddenColumns={ hiddenColumns }
          pricePermissions={ pricePermissions }
          filterOptions={ filterOptions }
          rowButtonProps={{
            buttonText    : 'DETAILS',
            buttonColour  : 'primary',
            onButtonClick : handleSelectRow,
            extraButtons  : inventoryActionButtons
          }}
          onFetchData={ handleFetchInventories }
        />
      </Card>

      <SendPhotoLinkEmailModal
        isOpen={ !!pendingPhotoLinkEmail }
        product={ pendingPhotoLinkEmail }
        onClose={ () => setPendingPhotoLinkEmail(null) }
        onSent={ () => {
          pageUtils.setAlertMsg('Successfully input photo link and send email.', ALERT_TYPES.SUCCESS)
          bumpSearchVersion()
        } }
      />

      <BulkMarkSoldOnHoldModal
        isOpen={ isBulkMarkStatusOpen }
        toggle={ () => setIsBulkMarkStatusOpen(false) }
        pageUtils={ pageUtils }
        onCompleted={ bumpSearchVersion }
      />


      <Modal isOpen={ !!pendingMarkStatus } toggle={ closeMarkSoldOnHold } centered>
        <ModalHeader toggle={ isMarkingStatus ? undefined : closeMarkSoldOnHold }>
          Mark Product as Sold or On Hold
        </ModalHeader>
        <ModalBody>
          { pendingMarkStatus && (
            <div className='mb-3'>
              <div><strong>SKU:</strong> { pendingMarkStatus.product_sku || pendingMarkStatus.inventory_sku || '-' }</div>
              <div><strong>Product:</strong> { pendingMarkStatus.product_name || '-' }</div>
              <div><strong>SQ / IQ:</strong> { pendingMarkStatus.sellable_quantity ?? '-' } / { pendingMarkStatus.stored_quantity ?? '-' }</div>
            </div>
          ) }
          <FormGroup check className='mb-2'>
            <Label check>
              <Input type='radio' name='mark-status-action' value='sold' checked={ markStatusAction === 'sold' } disabled={ isMarkingStatus } onChange={ () => setMarkStatusAction('sold') } />
              {' '}Mark as Sold
            </Label>
            <div className='small text-muted ml-4'>BKC Photo-Only: boleh tanpa Deal jika barang terjual di seller; SQ dan IQ menjadi 0. Selain BKC: wajib termasuk dalam Deal aktif.</div>
          </FormGroup>
          <FormGroup check>
            <Label check>
              <Input type='radio' name='mark-status-action' value='on_hold' checked={ markStatusAction === 'on_hold' } disabled={ isMarkingStatus } onChange={ () => setMarkStatusAction('on_hold') } />
              {' '}Mark as On Hold
            </Label>
            <div className='small text-muted ml-4'>Requires SQ &gt; 0. SQ will be reserved to 0; IQ stays unchanged.</div>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color='secondary' disabled={ isMarkingStatus } onClick={ closeMarkSoldOnHold }>Cancel</Button>
          <Button color='primary' disabled={ isMarkingStatus } onClick={ submitMarkSoldOnHold }>
            { isMarkingStatus ? 'Processing...' : 'Submit' }
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={ !!pendingManualDelete } toggle={ closeManualDelete } centered>
        <ModalHeader toggle={ isManualDeleting ? undefined : closeManualDelete }>
          Manual Delete Inventory
        </ModalHeader>
        <ModalBody>
          <div className='alert alert-danger'>
            <strong>Owner exception only.</strong> Action ini akan membuat product Not Available / Deleted dan membuat Inventory Movement <strong>Manual Delete</strong>. IQ fisik tidak dikurangi.
          </div>

          { pendingManualDelete && (
            <div className='mb-3'>
              <div><strong>SKU:</strong> { pendingManualDelete.product_sku || pendingManualDelete.inventory_sku || '-' }</div>
              <div><strong>Product:</strong> { pendingManualDelete.product_name || '-' }</div>
              <div><strong>SQ / IQ:</strong> { pendingManualDelete.sellable_quantity ?? '-' } / { pendingManualDelete.stored_quantity ?? '-' }</div>
            </div>
          ) }

          <FormGroup>
            <Label>Reason <span className='text-danger'>*</span></Label>
            <Input
              type='select'
              value={ manualDeleteReason }
              disabled={ isManualDeleting }
              onChange={ (event) => setManualDeleteReason(event.target.value) }
            >
              <option value=''>Select reason</option>
              { MANUAL_DELETE_REASON_OPTIONS.map((option) => (
                <option key={ option.value } value={ option.value }>{ option.label }</option>
              )) }
            </Input>
          </FormGroup>

          <FormGroup>
            <Label>Note{ manualDeleteReason === 'other' ? <span className='text-danger'> *</span> : null }</Label>
            <Input
              type='textarea'
              rows='3'
              value={ manualDeleteNote }
              disabled={ isManualDeleting }
              placeholder='Optional operational note'
              onChange={ (event) => setManualDeleteNote(event.target.value) }
            />
          </FormGroup>

          { manualDeleteReason === 'fake_product' && (
            <div className='alert alert-warning mb-0'>
              Untuk BKR yang confirmed fake, gunakan <strong>Mark BKR as BKX</strong> dari Inventory Detail bila tersedia. BKX menjaga IQ dan ownership/Consignor Return history dengan semantic yang lebih tepat.
            </div>
          ) }
        </ModalBody>
        <ModalFooter>
          <Button color='secondary' disabled={ isManualDeleting } onClick={ closeManualDelete }>Cancel</Button>
          <Button color='danger' disabled={ isManualDeleting || !manualDeleteReason } onClick={ submitManualDelete }>
            { isManualDeleting ? 'Deleting...' : 'Confirm Manual Delete' }
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={ !!pendingUndoDelete } toggle={ closeUndoDelete } centered>
        <ModalHeader toggle={ isUndoDeleting ? undefined : closeUndoDelete }>
          Undo Delete Inventory
        </ModalHeader>
        <ModalBody>
          <div className='alert alert-warning'>
            <strong>Owner only.</strong> Action ini membuat Inventory Movement <strong>Undo Delete</strong>, mengembalikan SQ yang sebelumnya dihapus, dan tidak menambah IQ.
          </div>

          { pendingUndoDelete && (
            <div>
              <div><strong>SKU:</strong> { pendingUndoDelete.product_sku || pendingUndoDelete.inventory_sku || '-' }</div>
              <div><strong>Product:</strong> { pendingUndoDelete.product_name || '-' }</div>
              <div><strong>SQ / IQ:</strong> { pendingUndoDelete.sellable_quantity ?? '-' } / { pendingUndoDelete.stored_quantity ?? '-' }</div>
            </div>
          ) }

          <div className='small text-muted mt-3'>
            Undo diblokir bila barang masih terikat Deal aktif / Consignor Return, IQ sudah 0, atau record adalah protected swap/archive seperti BKR → BKX.
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color='secondary' disabled={ isUndoDeleting } onClick={ closeUndoDelete }>Cancel</Button>
          <Button color='warning' disabled={ isUndoDeleting } onClick={ submitUndoDelete }>
            { isUndoDeleting ? 'Restoring...' : 'Confirm Undo Delete' }
          </Button>
        </ModalFooter>
      </Modal>

      <SwapBkrToBkxConfirmationModal
        isOpen={ !!pendingBkrToBkx }
        toggle={ closeBkrToBkx }
        sku={ pendingBkrToBkx?.product_sku || pendingBkrToBkx?.inventory_sku || 'This product' }
        handleConfirmation={ submitBkrToBkx }
      />
    </>
  )
}

InventoryListPage.propTypes = propTypes

export default InventoryListPage
